import { useState } from "react";
import { ChevronDown, ChevronRight, Database, RefreshCw, Zap } from "lucide-react";
import DataPathTree from "./DataPathTree";
import type { LastExecutionData } from "../../hooks/useLastExecutionData";

interface AvailableDataPanelProps {
  data: LastExecutionData | null;
  loading: boolean;
  onRefresh: () => void;
  /** id → nombre visible de los nodos del canvas. */
  nodeNames: Record<string, string>;
  /** Nodo actualmente seleccionado: su propio output no se ofrece (no puede referenciarse a sí mismo). */
  excludeNodeId?: string;
  /**
   * Si está definido (editor de Switch), el output de ESTE nodo se copia como path relativo
   * ("body.tier") — el formato que esperan las condiciones — en vez de la expresión completa.
   */
  relativeForNodeId?: string;
  /**
   * Sintaxis del texto copiado: `expression` para las propiedades `{{ }}` (default) o
   * `javascript` para el snippet de un nodo Transform, donde las llaves no se evalúan.
   */
  syntax?: "expression" | "javascript";
}

function Group({
  title,
  hint,
  defaultOpen,
  children,
}: {
  title: string;
  hint?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="rounded-lg border border-black/5 bg-white/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1.5 px-2.5 py-2 text-left hover:bg-black/[0.02] transition-colors"
      >
        {open ? (
          <ChevronDown className="size-3 text-foreground/30 shrink-0" />
        ) : (
          <ChevronRight className="size-3 text-foreground/30 shrink-0" />
        )}
        <span className="text-[11px] font-semibold text-foreground/70 truncate">{title}</span>
        {hint && <span className="ml-auto text-[10px] text-foreground/35 shrink-0">{hint}</span>}
      </button>
      {open && <div className="px-2.5 pb-2.5">{children}</div>}
    </div>
  );
}

/**
 * "Datos disponibles": estructura real observada en la última ejecución (payload del trigger
 * y output de cada nodo). Clic en un campo copia la expresión lista para pegar.
 */
export default function AvailableDataPanel({
  data,
  loading,
  onRefresh,
  nodeNames,
  excludeNodeId,
  relativeForNodeId,
  syntax = "expression",
}: AvailableDataPanelProps) {
  const js = syntax === "javascript";
  const triggerHint = js ? "trigger.payload.*" : "{{ trigger.payload.* }}";
  const nodeHint = js ? "nodes[…].output.*" : "{{ nodes.….output.* }}";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-foreground/40 flex items-center gap-1.5">
          <Database className="size-3" />
          Datos disponibles
        </p>
        <button
          type="button"
          onClick={onRefresh}
          className="p-1 rounded hover:bg-black/5 transition-colors"
          title="Recargar desde la última ejecución"
          aria-label="Recargar"
        >
          <RefreshCw className={`size-3 text-foreground/40 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!data ? (
        <p className="text-[11px] text-foreground/40 leading-relaxed">
          {loading
            ? "Cargando última ejecución…"
            : "Ejecuta la automatización una vez para explorar aquí la estructura real de los datos y copiar expresiones con un clic."}
        </p>
      ) : (
        <>
          <p className="text-[10px] text-foreground/35">
            De la ejecución del {new Date(data.enqueuedAtUtc).toLocaleString()} — clic en un campo
            para copiar la expresión.
          </p>

          {data.trigger !== undefined && (
            <Group
              title="Trigger (payload)"
              hint={triggerHint}
              defaultOpen={relativeForNodeId === undefined}
            >
              <DataPathTree
                value={data.trigger}
                buildCopyText={(path) =>
                  js ? `trigger.payload.${path}` : `{{ trigger.payload.${path} }}`
                }
              />
            </Group>
          )}

          {Object.entries(data.nodeOutputs)
            .filter(([nodeId]) => nodeId !== excludeNodeId)
            .map(([nodeId, output]) => {
              const relative = nodeId === relativeForNodeId;
              return (
                <Group
                  key={nodeId}
                  title={nodeNames[nodeId] ?? nodeId.slice(0, 8)}
                  hint={relative ? "campo para condiciones" : nodeHint}
                  defaultOpen={relative}
                >
                  {relative && (
                    <p className="text-[10px] text-foreground/40 mb-1.5 flex items-center gap-1">
                      <Zap className="size-2.5" />
                      Copia el path relativo, el formato de las condiciones del Switch.
                    </p>
                  )}
                  <DataPathTree
                    value={output}
                    buildCopyText={(path) =>
                      relative
                        ? path
                        : js
                          ? `nodes[${JSON.stringify(nodeId)}].output.${path}`
                          : `{{ nodes.${nodeId}.output.${path} }}`
                    }
                  />
                </Group>
              );
            })}
        </>
      )}
    </div>
  );
}
